import type { CategoryNode } from '@/api/address-book/types';
import { type BadgeColor, type DotsColor, theme } from '@/shared/design-system';

/** A category is never rendered as a disabled badge. */
export type CategoryBadgeColor = Exclude<BadgeColor, 'disabled'>;

const CATEGORY_BADGE_COLORS: CategoryBadgeColor[] = [
  'primary',
  'yellow',
  'secondary',
  'orange',
  'purple',
  'destructive',
];

export const getCategoryVariant = (rootName: string): CategoryBadgeColor => {
  let hash = 5381;
  for (let i = 0; i < rootName.length; i++) {
    hash = (((hash << 5) + hash) ^ rootName.charCodeAt(i)) >>> 0;
  }
  return CATEGORY_BADGE_COLORS[hash % CATEGORY_BADGE_COLORS.length];
};

export const CATEGORY_VARIANT_COLORS: Record<CategoryBadgeColor, string> = {
  primary: theme.colors.brand.solid,
  yellow: theme.colors.warning.solid,
  secondary: theme.colors.info.solid,
  orange: theme.colors.orange.solid,
  purple: theme.colors.accent.solid,
  destructive: theme.colors.error.solid,
  default: theme.colors.border.default,
};

export const CATEGORY_VARIANT_TEXT_COLORS: Record<CategoryBadgeColor, string> = {
  primary: theme.colors.brand.text,
  yellow: theme.colors.warning.text,
  secondary: theme.colors.info.text,
  orange: theme.colors.orange.text,
  purple: theme.colors.accent.text,
  destructive: theme.colors.error.text,
  default: theme.colors.text.primary,
};

export const CATEGORY_VARIANT_DOT_COLORS: Record<CategoryBadgeColor, DotsColor> = {
  primary: 'green',
  yellow: 'orange',
  secondary: 'blue',
  orange: 'orange',
  purple: 'purple',
  destructive: 'red',
  default: 'grey',
};

export const CATEGORY_VARIANT_SUBTLE_COLORS: Record<CategoryBadgeColor, string> = {
  primary: theme.colors.brand.subtle,
  yellow: theme.colors.warning.subtle,
  secondary: theme.colors.info.subtle,
  orange: theme.colors.orange.subtle,
  purple: theme.colors.accent.subtle,
  destructive: theme.colors.error.subtle,
  default: theme.colors.background.overlay,
};

export const getAllIds = (nodes: CategoryNode[]): string[] =>
  nodes.flatMap((n) => [n.id, ...getAllIds(n.children)]);

export const hasOpenCategory = (nodes: CategoryNode[]): boolean =>
  nodes.some((node) => !node.locked || hasOpenCategory(node.children));

const collectOpenIds = (nodes: CategoryNode[], into: Set<string>): Set<string> => {
  nodes.forEach((node) => {
    if (!node.locked) into.add(node.id);
    collectOpenIds(node.children, into);
  });
  return into;
};

export const selectsOpenCategory = (
  nodes: CategoryNode[],
  selectedIds: readonly string[],
): boolean => {
  const openIds = collectOpenIds(nodes, new Set());
  return selectedIds.some((id) => openIds.has(id));
};
